import React, { useState, useRef, useEffect } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  getStep1Questions,
  maritalStatusOptions,
  step1Part2Questions,
  exemptionDropdownQuestion,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function ConnecticutForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  getPdfUrl,
  getInitialQuestions,
  version,
  formLink,
  isOnboardingV2,
}) {
  // start at step 1
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  // pre-fill applicant name
  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  // create step 1 form:
  const step1Form = useRef();
  const step1Qs = getStep1Questions(version);
  const step1InitQ = getInitialQuestions(step1Qs);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitQ);

  useEffect(() => {
    setStepOneQuestions(step1InitQ);
  }, [version]);

  // handle withholding questions that show up depending on marital status
  const withholdingCodeForm = useRef();
  const [withholdingCodeQuestionsError, setWithholdingCodeQuestionsError] = useState(false);

  const getMaritalStatusOptions = (questions) => {
    if (questions[8].answer.answer.filing_status !== '' || data.filing_status) {
      const fs = questions[8].answer.answer.filing_status || data.filing_status;
      const withholdingQuestions = maritalStatusOptions[fs];
      const withholdingQuestionsWithAnswers = getInitialQuestions(withholdingQuestions);
      return withholdingQuestionsWithAnswers;
    }
  };
  const [withholdingCodeQuestions, setWithholdingCodeQuestions] = useState(
    data.filing_status ? getMaritalStatusOptions(stepOneQuestions) : [],
  );

  // show withholding code questions
  const renderMaritalStatusOptions = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={withholdingCodeQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = withholdingCodeQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWithholdingCodeQuestions(questions);
        }}
        ref={withholdingCodeForm}
      />
    </Flex>
  );

  // show step 1 form:
  const renderStep1Form = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
          setWithholdingCodeQuestions(getMaritalStatusOptions(questions));
        }}
        ref={step1Form}
      />
      {stepOneQuestions[8].answer.answer.filing_status !== ''
          && renderMaritalStatusOptions(isReviewStep)}
    </Flex>
  );

  // create form for step 1 part 2 questions
  const step1Part2Form = useRef();
  const step1Part2InitQ = getInitialQuestions(step1Part2Questions);
  const [stepOnePartTwoQuestions, setStepOnePartTwoQuestions] = useState(step1Part2InitQ);

  const renderStep1Part2Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOnePartTwoQuestions}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOnePartTwoQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOnePartTwoQuestions(questions);
        }}
        ref={step1Part2Form}
      />
    </div>
  );

  const [claimingExemption, setClaimingExemption] = useState(
    data.claiming_exemption || false,
  );

  const stateOfResidenceForm = useRef();
  const stateOfResidenceInitQ = getInitialQuestions(exemptionDropdownQuestion);
  const [stateOfResidenceOrDomicile, setStateOfResidenceOrDomicile] = useState(
    stateOfResidenceInitQ,
  );
  const [stateError, setStateError] = useState(false);

  const renderExemptionFromWithholding = (isReviewStep) => (
    <div>
      <Flex style={{ marginBottom: '20px' }} column>
        <S.QuestionLabel style={{ marginBottom: '5px' }}>
          Exemption from withholding
        </S.QuestionLabel>
        <Flex column>
          <label>
            <Flex>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={claimingExemption}
                disabled={isReviewStep}
                onChange={(e) => {
                  setClaimingExemption(!claimingExemption);
                }}
                checked={claimingExemption}
              />
              <Box ml={1} />
              Check if you are claiming the MSRRA exemption.
            </Flex>
          </label>
        </Flex>
      </Flex>
      {claimingExemption && (
      <Flex style={{ marginBottom: '20px' }} column>
        <FormConsumer
          questions={stateOfResidenceOrDomicile}
          {...(isReviewStep && {
            viewResultAsManager: true,
            reviewActions: [],
          })}
          onQuestionChange={({ question, value }) => {
            const questions = stateOfResidenceOrDomicile.map((q) => {
              if (q.id !== question.id) return q;
              const answer = {
                ...q.answer,
                answer: {
                  ...q.answer.answer,
                  [q.id]: value,
                },
              };
              const updatedQuestion = { ...q, answer };
              return updatedQuestion;
            });
            setStateOfResidenceOrDomicile(questions);
          }}
          ref={stateOfResidenceForm}
        />
      </Flex>
      )}
    </div>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {renderStep1Part2Form()}
          {renderExemptionFromWithholding()}
        </Flex>
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column mt={10} mb={10}>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (step1Part2Form && step1Part2Form.current) {
              step1Part2Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (withholdingCodeForm && withholdingCodeForm.current) {
              withholdingCodeForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (withholdingCodeQuestionsError) {
              setWithholdingCodeQuestionsError(true);
              err = true;
            } else {
              setWithholdingCodeQuestionsError(false);
            }
            if (claimingExemption && stateError) {
              setStateError(true);
              err = true;
            } else {
              setStateError(false);
            }
            if (err) {

            } else {
              // No error, proceed to save
              let exemptionData;
              const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
              const stepOnePartTwoData = stepOnePartTwoQuestions.reduce(
                (acc, question) => ({ ...acc, ...question.answer.answer }),
                {},
              );
              const maritalStatusData = withholdingCodeQuestions.reduce(
                (acc, question) => ({ ...acc, ...question.answer.answer }),
                {},
              );
              const claimExemptionData = {
                claiming_exemption: claimingExemption,
              };
              const exemptionStateData = stateOfResidenceOrDomicile.reduce(
                (acc, question) => ({ ...acc, ...question.answer.answer }),
                {},
              );
              exemptionData = { ...claimExemptionData, ...exemptionStateData };
              const data = {
                ...stepOneData,
                ...maritalStatusData,
                ...stepOnePartTwoData,
                ...exemptionData,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {renderStep1Part2Form(true)}
        {renderExemptionFromWithholding(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  // show step 3: hellosign
  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );
  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default ConnecticutForm;
